import { useState, useEffect, useMemo } from "react";
import { useLocation, useParams } from 'react-router-dom';
import styles from "./SportsOddsDetails.module.css";
import Section2 from "../section2/Section2";
import BetLock from "../../../../components/betlock";
import UserBookModal from "../../../../components/userBookModal";
import { getMatchExposureSummary } from "../../../../apiServices/userBookService";

// Lock Icon Component - White color
const LockIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    width="16" 
    height="16"
    fill="#ffffff"
    style={{ opacity: 0.9 }}
  >
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
  </svg>
);

export default function SportsOddsDetails({ sportData }) {
  const [loading, setLoading] = useState(true);
  const [exposureIds, setExposureIds] = useState({});
  const [bookModal, setBookModal] = useState({ show: false, market: null });
  const location = useLocation();
  const { sid, gmid } = useParams();
  const eventName = location.state?.eventName || "Unknown Event";

  // Helper: Format volume
  const formatVolume = (size) => {
    if (!size) return "0";
    if (size >= 1000000) return (size / 1000000).toFixed(1) + "M";
    if (size >= 1000) return (size / 1000).toFixed(0) + "K";
    return size.toString();
  };

  // Transform API data to Bookmaker format
  const transformApiData = (apiData) => {
    let marketArray = [];
    
    // Handle different data structures
    if (Array.isArray(apiData)) {
      marketArray = apiData;
    } else if (apiData?.data && Array.isArray(apiData.data)) {
      marketArray = apiData.data;
    } else if (apiData && typeof apiData === 'object') {
      const values = Object.values(apiData);
      if (values.length > 0 && Array.isArray(values[0])) {
        marketArray = values[0];
      }
    }

    if (!marketArray || marketArray.length === 0) {
      return [];
    }

    return marketArray.map((market) => {
      const mname = market.mname || market.marketName || market.name || "Unknown";
      const minMax = market.min && market.max 
        ? `Min:${market.min} Max:${formatVolume(market.max)}`
        : "Min:100 Max:50K";

      const sections = market.section || market.sections || [];
      
      // Determine market type for exposure lookup
      let marketType = 'match_odds';
      const nameLower = mname.toLowerCase();
      if (nameLower.includes('bookmaker')) marketType = 'bookmaker';
      else if (nameLower.includes('fancy') || nameLower.includes('session')) marketType = 'session';

      const rows = sections.map((section) => {
        const odds = section.odds || [];
        const backOdd = odds.find(o => o.otype === 'back' && o.tno === 0) ||
                       odds.find(o => o.otype === 'back');
        const layOdd = odds.find(o => o.otype === 'lay' && o.tno === 0) ||
                      odds.find(o => o.otype === 'lay');

        const isSuspended = section.gstatus === 'SUSPENDED' || 
                           section.gstatus === 'Ball Running' ||
                           market.status === 'SUSPENDED';

        const convertOdds = (oddsValue) => {
          if (!oddsValue || oddsValue <= 0) return null;
          if (oddsValue > 100) return (oddsValue / 100).toFixed(2);
          return parseFloat(oddsValue).toFixed(2);
        };

        const runnerName = section.nat || section.name || '';

        const marketId = market.mid || market.id;
        const expKey = `${marketId}_${runnerName}`;
        const exposureVal = exposureIds[expKey] || 0;

        return {
          name: runnerName,
          exposure: exposureVal,
          pl: 0,
          backOdds: convertOdds(backOdd?.odds),
          backVolume: formatVolume(backOdd?.size),
          layOdds: convertOdds(layOdd?.odds),
          layVolume: formatVolume(layOdd?.size),
          isSuspended
        };
      });

      return {
        mname,
        minMax,
        rows,
        marketId: market.mid || market.id
      };
    });
  };

  const transformedMarkets = useMemo(() => transformApiData(sportData), [sportData, exposureIds]);

  useEffect(() => {
    if (sportData) {
      setLoading(false);
    }
  }, [sportData]);

  // Fetch Exposure Summary
  useEffect(() => {
    const fetchExposure = async () => {
        const token = localStorage.getItem("ownerToken") || localStorage.getItem("staffToken") || localStorage.getItem("token");
        if (!token || !gmid) return;

        const response = await getMatchExposureSummary(token, gmid);
        if (response.success && response.data) {
            const newMap = {};
            response.data.forEach(group => {
                const cat = (group.category || 'match_odds').toLowerCase(); 
                const markets = group.markets || [];
                markets.forEach(m => {
                    const marketId = m.matchId; // This is the Market ID from backend
                    Object.entries(m.selections || {}).forEach(([runner, val]) => {
                         // Use Market ID + Runner Name for unique identification
                         newMap[`${marketId}_${runner}`] = val;
                    });
                });
            });
            setExposureIds(newMap);
        }
    };

    fetchExposure();
    const interval = setInterval(fetchExposure, 5000); 
    return () => clearInterval(interval);
  }, [gmid]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.section1}>
          <div className={styles.loadingBox}>Loading Markets...</div>
        </div>
      </div>
    );
  }

  const handleBook = (market) => {
    setBookModal({ show: true, market });
  };

  if (!sportData || transformedMarkets.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.section1}>
          <div className={styles.loadingBox}>No markets available</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.section1}>
        {transformedMarkets.map((market, marketIdx) => (
          <div key={marketIdx} className={styles.marketCard}>
            {/* Market Header */}
            <div className={styles.marketHeader}>
              <span className={styles.marketName}>{market.mname}</span>
              <div className={styles.headerButtons}>
                <button className={styles.bookBtn} onClick={() => handleBook(market)}>Book</button>
               
                <BetLock 
                  sportId={sid} 
                  matchId={gmid} 
                  marketId={market.marketId} 
                  marketName={market.mname} 
                />
              </div>
            </div>

            {/* Min/Max Row with Back/Lay Headers */}
            <div className={styles.minMaxRow}>
              <div className={styles.minMaxText}>{market.minMax}</div>
              <div className={styles.oddsHeaders}>
                <div className={styles.backHeader}>Back</div>
                <div className={styles.layHeader}>Lay</div>
              </div>
            </div>

            {/* Team Rows */}
            {market.rows.map((row, rowIdx) => (
              <div 
                key={rowIdx} 
                className={`${styles.teamRow} ${row.isSuspended ? styles.suspended : ''}`}
              >
                <div className={styles.teamInfo}>
                  <span className={styles.teamName}>{row.name}</span>
                  <span className={`${styles.exposure} ${row.exposure >= 0 ? styles.positive : styles.negative}`}>
                    {row.exposure.toFixed(2)}
                  </span>
                </div>
                <div className={`${styles.plValue} ${row.pl >= 0 ? styles.plPositive : styles.plNegative}`}>
                  {row.pl !== 0 ? row.pl.toFixed(2) : ''}
                </div>
                <div className={styles.oddsContainer}>
                  {row.isSuspended ? (
                    <div className={styles.suspendedOverlay}>
                      SUSPENDED
                    </div>
                  ) : (
                    <>
                      <div className={styles.backCell}>
                        <span className={styles.odds}>{row.backOdds || '—'}</span>
                        <span className={styles.volume}>{row.backVolume}</span>
                      </div>
                      <div className={styles.lockedCell}>
                        <LockIcon />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className={styles.section2}>
        <Section2 
          selectedBet={null} 
          onCloseBet={() => {}} 
          onBetSubmit={() => {}} 
        />
      </div>
      
      {bookModal.show && bookModal.market && (
        <UserBookModal 
            onClose={() => setBookModal({ show: false, market: null })}
            matchId={gmid}
            marketId={bookModal.market.marketId}
            marketName={bookModal.market.mname}
            runners={bookModal.market.rows.map(r => r.name)}
        />
      )}
    </div>
  );
}